// 剣舞の絵ごとの色の差をならす比較版 (10-23 作者「ボスの色が安定してない。画像のせい？調整できる？」)。
//   測ると画像のせいだった: 立ち姿 idle だけが暗く地味で、攻撃の絵は銀と藤が明るく色も濃い (三連 v7 が一番強い)。
//   絵が替わるたびに明るさと色の濃さが跳ぶ。描く時に絵ごとの ctx.filter (brightness・saturate) でそろえる。
//   測り方 = scripts/measure_blade_dancer_color_1023.py (銀と藤の明るさの平均・全体の彩度)。記録 = docs/research/2026-10-23-blade-dancer-trail-length-color.md
//   配布版は 'now' (補正なし)。DEV ?bladeColor=idle で立ち姿の色へ、=mid で全部の絵の真ん中の色へそろえる。

export type BladeColor = 'now' | 'idle' | 'mid'

export function parseBladeColor(v: string | null): BladeColor {
  return v === 'idle' || v === 'mid' ? v : 'now'
}

/** 絵のファイル名 → [銀と藤の明るさの平均 (0〜255), 全体の彩度 (0〜1)]。 */
export const BLADE_DANCER_COLOR_MEASURED: Readonly<Record<string, readonly [number, number]>> = {
  idle: [155.05, 0.272],
  // 10-29 Codex 描きの3枚 (幕引きの礼・崩れの入り・拍手の入り)
  m1029_exec_bow: [163.5, 0.331],
  m1029_defeat_fall: [164.5, 0.32],
  m1029_clap_in: [168.5, 0.315],
  // 11-01 δ の低い払いの専用の絵 v1 (作者が「突きに見える」で通さなかった・public に残して使っていない)
  m1101_delta_sweep: [174, 0.345],
  // 11-01 δ の低い払いの専用の絵 v2 (配布版の既定)
  m1101_delta_sweep_v2: [169.8, 0.374],
  // 11-02 γ の戻りの残心の専用の絵 (開発版 ?bladeGammaRest=v1)
  m1102_gamma_rest: [174.55, 0.325],
  // 11-03 大振り・速い連の型の1枚 (開発版 ?bladeGrandQuick=v1)
  m1103_grand_g1: [172.25, 0.303],
  m1103_quick_d2: [163.3, 0.302],
  // 11-03 速い連の回る型 (開発版 ?bladeQuick=spin1)
  m1103_qspin_s1: [172.5, 0.321],
  m1103_qspin_s2: [170, 0.324],
  m1103_qspin_s3: [171, 0.311],
  m1103_qspin_h2: [175, 0.345],
  m1103_qspin_h4: [175.5, 0.31],
  // 11-04 速い連の交互打ちの2発目の型 (開発版 ?bladeQuick=alt1)
  m1104_qalt_h2: [174, 0.315],
  // 11-04 速い連の専用の絵の型 (答えるページ待ち・まだゲームに入れていない)
  m1104_quick_e3: [175.5, 0.324],
  // 11-04 型の剣の長さ直し (作者 type=fix「先に出してる剣が異様に短い」・刃は背丈の約36%/40%)
  m1104_quick_e3b: [178, 0.321],
  // 11-04 速い連の1発目の当たりの型を細い姿で描き直し (作者 first=redraw・足の開き0.04)
  m1104_quick_d2: [180, 0.323],
  // 11-04 1発目の当たりを「斬り抜けた直後」で描き直し (作者 d2=redo「凛として敵を見据えながら構えているように見える」)
  m1104_quick_d2c: [177, 0.318],
  // 11-04 速い連の残り (3発目の型 e3b と同じ姿で描く・作者 e3=ok)
  m1104_quick_e1: [179.5, 0.300],
  m1104_quick_f3: [173, 0.302],
  m1104_quick_f1: [177.5, 0.304],
  m1104_quick_e4: [178, 0.311],
  m1104_quick_d4: [179, 0.312],
  m1104_quick_f2: [175, 0.328],
  m1104_quick_e2: [176, 0.322],
  // 11-04 速い連の1発目の振りかぶりと振り抜け (作者 d2=ok の型で描く)
  m1104_quick_d1: [165.5, 0.318],
  m1104_quick_d3: [173, 0.320],
  // 11-03 大振りの締めの戻り2枚 (開発版 ?bladeGrandQuick=v3)
  m1103_grand_g2: [176.2, 0.331],
  m1103_grand_g3: [170.55, 0.308],
  m1102_quick_sig: [164.5, 0.306], // 11-02 測りの道具では d2 より明るさ +1・彩度 +0.004 なので d2 の値から比で出した
  defeat_1019: [164.6, 0.32],
  exec_parried_1021: [179.1, 0.353],
  exec_slash: [157.9, 0.306],
  glide: [167, 0.323],
  hem_rise: [163.4, 0.299],
  hem_sweep: [160, 0.288],
  m1020_v6_a1: [169.1, 0.317],
  m1020_v6_a2: [166.9, 0.313],
  m1020_v6_a3: [166.35, 0.314],
  m1020_v6_a4: [166.2, 0.311],
  m1020_v6_b1: [166.6, 0.308],
  m1020_v6_b2: [163.6, 0.326],
  m1020_v6_b3: [168.85, 0.3],
  m1020_v6_b4: [166.45, 0.303],
  m1020_v6_c1: [162.7, 0.314],
  m1020_v6_c2: [165.7, 0.299],
  m1020_v6_c3: [165.7, 0.302],
  m1020_v6_c4: [167.2, 0.288],
  m1020_v6_d1: [164.6, 0.308],
  m1020_v6_d2: [171.5, 0.287],
  m1020_v6_d3: [169.45, 0.335],
  m1020_v6_d4: [165.3, 0.326],
  m1020_v6_e1: [168.35, 0.325],
  m1020_v6_e2: [170.45, 0.32],
  m1020_v6_e3: [164.55, 0.337],
  m1020_v6_e4: [168.55, 0.3],
  m1020_v6_f1: [173.55, 0.32],
  m1020_v6_f2: [173.65, 0.285],
  m1020_v6_f3: [172.75, 0.303],
  m1020_v6_f4: [171.05, 0.291],
  m1020_v6_g1: [169.9, 0.305],
  m1020_v6_g2: [168.5, 0.297],
  m1020_v6_g3: [169.75, 0.299],
  m1020_v6_g4: [164.35, 0.286],
  m1020_waltz_1: [166.9, 0.316],
  m1020_waltz_10: [173.45, 0.328],
  m1020_waltz_11: [176.75, 0.313],
  m1020_waltz_12: [171.45, 0.319],
  m1020_waltz_2: [168.75, 0.331],
  m1020_waltz_3: [167.35, 0.327],
  m1020_waltz_4: [166.2, 0.323],
  m1020_waltz_5: [175.8, 0.334],
  m1020_waltz_6: [172.6, 0.346],
  m1020_waltz_7: [171.3, 0.348],
  m1020_waltz_8: [174.35, 0.339],
  m1020_waltz_9: [174.35, 0.336],
  m1020_waltz_a1: [167.3, 0.297],
  m1020_waltz_a2: [169.75, 0.296],
  m1020_waltz_b1: [171.3, 0.349],
  m1020_waltz_b2: [170.8, 0.348],
  m1020_waltz_b3: [173.7, 0.331],
  m1020_waltz_c1: [173.6, 0.301],
  m1020_waltz_c2: [171.55, 0.37],
  m1020_waltz_c3: [174.05, 0.319],
  m1020_waltz_c4: [168.95, 0.309],
  m1020_waltz_c5: [176.45, 0.342],
  m1020_waltz_c6: [165, 0.299],
  m1020_waltz_s1: [156.45, 0.279],
  m1020_waltz_s2: [146.25, 0.276],
  m1020_waltz_w1: [164.1, 0.305],
  m1020_waltz_w2: [163.65, 0.304],
  m1020_waltz_w3: [164.6, 0.307],
  m1022_w7_a1: [173.4, 0.352],
  m1022_w7_a2: [174.4, 0.355],
  m1022_w7_a3: [173.7, 0.341],
  m1022_w7_a4: [174.25, 0.323],
  m1022_w7_b1: [175.2, 0.336],
  m1022_w7_b2: [173.25, 0.345],
  m1022_w7_b3: [176.8, 0.333],
  m1022_w7_b4: [170.5, 0.33],
  m1022_w7_c1: [173.15, 0.33],
  m1022_w7_c2: [174.55, 0.348],
  m1022_w7_c3: [175.45, 0.33],
  m1022_w7_c4: [170.2, 0.349],
  m1025_p4_1: [158.35, 0.326],
  m1025_p4_2: [161.2, 0.307],
  m1025_p4_3: [162, 0.306],
  m1025_p4_4: [159, 0.303],
  m1025_p4_5: [158.45, 0.286],
  m1025_p4_6: [155.2, 0.305],
  m1025_p4_7: [156, 0.292],
  m1025_p4_8: [156.1, 0.288],
  m1025_g_1: [169, 0.307],
  m1025_g_2: [171.2, 0.316],
  m1025_g_3: [175.75, 0.308],
  m1025_g_4: [173.45, 0.303],
  m1025_c_1: [169.05, 0.341],
  m1025_c_2: [170.3, 0.347],
  m1025_c_3: [172.8, 0.331],
  m1025_c_5: [168.5, 0.326],
  m1025_d_1: [162.25, 0.349],
  m1025_d_2: [161.75, 0.337],
  m1025_d_3: [168.25, 0.31],
  m1025_d_4: [166.6, 0.33],
  m1025_r_1: [173.5, 0.328],
  m1025_r_2: [175.75, 0.299],
  m1025_r_3: [175.4, 0.316],
  m1025_r_4: [172, 0.304],
  m1025_t_1: [168.8, 0.373],
  m1025_t_2: [170.8, 0.357],
  m1025_t_3: [169.8, 0.36],
  m1025_p4_6b: [164.35, 0.348],
  m1025_r_1b: [168.75, 0.333],
  m1025_w_1: [165.45, 0.333],
  m1025_w_2: [164.7, 0.329],
  m1025_w_3: [165.85, 0.312],
  m1025_w_4: [164.25, 0.324],
  m1025_k_1: [165.75, 0.299],
  m1025_k_2: [165.55, 0.306],
  m1026_t_4: [163, 0.33],
  m1026_p4_6c: [165.8, 0.278],
  m1028_s_side: [168.0, 0.328],
  m1028_s_side_m: [168.0, 0.328],
  m1028_s_back3q: [167.2, 0.326],
  m1028_s_back3q_m: [167.2, 0.326],
  m1028_s_back: [166.55, 0.319],
  m1028_s_back_m: [166.55, 0.319],
  m1028_s_front: [162.05, 0.314],
  m1028_s_front_m: [162.05, 0.314],
  // 10-28 夕 回る絵の2版目 (?bladeSpinTurn=2)。
  m1028_s2_side: [175, 0.312],
  m1028_s2_side_m: [175, 0.312],
  m1028_s2_back3q: [178.1, 0.32],
  m1028_s2_back3q_m: [178.1, 0.32],
  m1028_s2_back: [179, 0.321],
  m1028_s2_back_m: [179, 0.321],
  m1028_s2_front: [175.45, 0.315],
  m1028_s2_front_m: [175.45, 0.315],
  parried_1019: [160.35, 0.286],
  pirouette_thrust: [160.05, 0.286],
  pirouette_turn: [169.5, 0.338],
  pirouette_wind: [163.75, 0.312],
  staggered_1019: [162.7, 0.294],
  swap_draw: [168.4, 0.327],
  thrust_recover: [166.7, 0.307],
  waltz_cut1: [157.65, 0.312],
  waltz_cut2: [159.4, 0.269],
  waltz_cut2b: [161.45, 0.27],
  waltz_ready: [165.4, 0.274],
}

const median = (a: number[]): number => [...a].sort((x, y) => x - y)[Math.floor(a.length / 2)]!
const ALL = Object.values(BLADE_DANCER_COLOR_MEASURED)
const TARGET: Record<Exclude<BladeColor, 'now'>, readonly [number, number]> = {
  idle: BLADE_DANCER_COLOR_MEASURED.idle!,
  mid: [median(ALL.map(v => v[0])), median(ALL.map(v => v[1]))],
}

let mode: BladeColor = 'now'
export function useBladeColor(m: BladeColor): void { mode = m }
export function bladeColorMode(): BladeColor { return mode }

/** 剣舞の絵 file にかける ctx.filter。補正なし・測っていない絵は null。 */
export function bladeColorFilter(file: string): string | null {
  if (mode === 'now') return null
  const m = BLADE_DANCER_COLOR_MEASURED[file]
  if (!m) return null
  const [l, s] = TARGET[mode]
  const b = l / m[0], sat = s / m[1]
  if (Math.abs(b - 1) < 0.005 && Math.abs(sat - 1) < 0.005) return null
  return `brightness(${b.toFixed(3)}) saturate(${sat.toFixed(3)})`
}
